/**
 * Pure model behind the « Mur » look of the Présences sheet (#814).
 *
 * The geometry lives in `presence-wall-layout.ts`; this is everything else the wall needs to
 * decide before it can draw — which territory a student belongs to, in what order students
 * are laid along the path, what their cell says, and what the legend lists. All of it as
 * plain functions over plain data, because the frontend has no @vue/test-utils.
 */
import type { PresenceStatus } from '@/sdk/model';
import { darken, programHue, shade } from '@/utils/presence-wall-palette';
import { formatUserFullName, formatUserInitialName } from '@/utils/user-utils';
import { isRecorded } from './presence-quick-entry';
import type { StudentRow } from './presence-sheet-row';

/** How a student is named inside a cell. A setting, not a fixed choice (arbitrated 2026-09-12). */
export const WALL_NAME_FORMATS = [
  { value: 'firstInitial', title: 'Prénom + initiale' },
  { value: 'lastInitial', title: 'Nom + initiale' },
  { value: 'full', title: 'Prénom et nom' },
] as const;

export type WallNameFormat = typeof WALL_NAME_FORMATS[number]['value'];

/**
 * What the wall tiles by. The packing only knows two abstract levels — a major territory and
 * a minor one inside it — so an axis is just a pair of accessors.
 *
 * `status` is the one to watch: a cell changes territory the moment it is recorded, so the
 * geometry recomposes under the hand while entering. That is useful to finish a day (every
 * unrecorded student in one block) and disconcerting otherwise, which is why it is not the
 * default.
 */
export const WALL_GROUPINGS = [
  { value: 'program', title: 'Programme et groupe' },
  { value: 'competency', title: 'Programme et compétence' },
  { value: 'status', title: 'État et programme' },
  { value: 'none', title: 'Aucun' },
] as const;

export type WallGrouping = typeof WALL_GROUPINGS[number]['value'];

export function isWallGrouping(value: unknown): value is WallGrouping {
  return WALL_GROUPINGS.some(option => option.value === value);
}

/** Which term the students are ordered by, along the path inside their territory. */
export const WALL_SORTS = [
  { value: 'firstName', title: 'Prénom' },
  { value: 'lastName', title: 'Nom' },
] as const;

export type WallSort = typeof WALL_SORTS[number]['value'];

export function isWallNameFormat(value: unknown): value is WallNameFormat {
  return WALL_NAME_FORMATS.some(option => option.value === value);
}

export function isWallSort(value: unknown): value is WallSort {
  return WALL_SORTS.some(option => option.value === value);
}

/** The two name parts of a row, trimmed, with the email as the last resort. */
export function nameParts(row: StudentRow): { first: string; last: string } {
  const user = row.frequentation.student?.user;
  const first = user?.firstName?.trim() ?? '';
  const last = user?.lastName?.trim() ?? '';
  if (!first && !last) return { first: user?.email?.trim() ?? '', last: '' };
  return { first, last };
}

/**
 * What the cell says. `firstInitial` is the default that goes with the first-name sort the
 * centre asked for (#814, `[00:14:52]`): the eye looks for the term the list is ordered by.
 * The assembling itself is `user-utils`' job — UI-CHARTE.md §3.
 */
export function wallName(row: StudentRow, format: WallNameFormat): string {
  const user = row.frequentation.student?.user;
  if (format === 'full') return formatUserFullName(user ?? null);
  return formatUserInitialName(user, format === 'firstInitial' ? 'first' : 'last');
}

/** Comparer for the requested order — always tie-broken on the other term, so it is total. */
export function wallComparer(sort: WallSort): (a: StudentRow, b: StudentRow) => number {
  return (a, b) => {
    const pa = nameParts(a);
    const pb = nameParts(b);
    const [first, second] = sort === 'firstName'
      ? [[pa.first, pb.first], [pa.last, pb.last]]
      : [[pa.last, pb.last], [pa.first, pb.first]];
    return first[0].localeCompare(first[1], 'fr') || second[0].localeCompare(second[1], 'fr');
  };
}

/** Identifier of the programme a row belongs to — the major territory. */
export function programKey(row: StudentRow): string {
  return row.frequentation.learningProgramId;
}

export function programLabel(row: StudentRow): string {
  return row.frequentation.learningProgram?.title ?? 'Sans programme';
}

/**
 * Identifier of the group a row belongs to — the minor territory.
 *
 * A student with no group falls back on their programme, so the minor territory is never
 * empty and the tiling still has two levels. This is also what happens today whatever the
 * data says: `FrequentationService.ListByProgram` does not `.Include` the memberships, so
 * `groups` comes back empty from the day sheet. Fixing that belongs to the scope widening,
 * not here — the wall must read correctly either way.
 */
export function groupKey(row: StudentRow): string {
  const group = row.frequentation.groups?.[0];
  return group ? `g:${group.id}` : `p:${programKey(row)}`;
}

export function groupLabel(row: StudentRow): string {
  const group = row.frequentation.groups?.[0];
  return group?.name ?? 'Sans groupe';
}

/** One programme in the legend: its hue, its name, and how much of it is on screen. */
export interface WallLegendEntry {
  key: string;
  label: string;
  /** `r,g,b` triplet of the programme hue. */
  hue: string;
  students: number;
  /** Students whose cell for the shown period carries a recorded status. */
  recorded: number;
}

/**
 * The named legend above the wall (#814 asks for « un encadré ou un bandeau nommant le
 * programme »; the arbitration put the name here rather than inside the territory, where it
 * would have eaten space and moved with the geometry).
 *
 * Programmes come out in the order they are met, so the legend and the wall agree on ranks —
 * the rank is what picks the hue.
 */
export function wallLegend(
  students: readonly StudentRow[],
  isRecorded: (row: StudentRow) => boolean,
): WallLegendEntry[] {
  const byKey = new Map<string, WallLegendEntry>();
  for (const row of students) {
    const key = programKey(row);
    let entry = byKey.get(key);
    if (!entry) {
      byKey.set(key, (entry = {
        key,
        label: programLabel(row),
        hue: programHue(byKey.size),
        students: 0,
        recorded: 0,
      }));
    }
    entry.students++;
    if (isRecorded(row)) entry.recorded++;
  }
  return [...byKey.values()];
}

/** Rank of a programme in the legend — what `packWall` orders the major territories by. */
export function programRanks(legend: readonly WallLegendEntry[]): Map<string, number> {
  return new Map(legend.map((entry, i) => [entry.key, i]));
}

export interface TerritoryPaint {
  /** `r,g,b` of the fill. */
  fill: string;
  /** `r,g,b` of the stroke — the same step, darkened. */
  stroke: string;
}

/**
 * Fill and stroke of one group's territory: the programme hue, stepped by the group's rank
 * WITHIN that programme. Stepping by a global rank would give two groups of different
 * programmes the same step and lose the family resemblance.
 */
export function territoryPaint(programRank: number, groupRank: number): TerritoryPaint {
  const stepped = shade(programHue(programRank), groupRank);
  return { fill: stepped, stroke: darken(stepped) };
}

/**
 * Hue ranks of the territories, on whichever axis is chosen: one per major, and one per minor
 * WITHIN its major. Ranking minors globally would give two groups of different programmes the
 * same step and lose the family resemblance.
 *
 * Walk every student, not only the visible ones: hiding a programme must not repaint the ones
 * that stay, and on the `program` axis this is what keeps a territory the colour of its own
 * token in the legend.
 */
export function territoryRanks(
  students: readonly StudentRow[],
  placementOf: (row: StudentRow) => WallPlacement,
): { major: Map<string, number>; minor: Map<string, number> } {
  const major = new Map<string, number>();
  const minor = new Map<string, number>();
  const perMajor = new Map<string, number>();
  for (const row of students) {
    const placement = placementOf(row);
    if (!major.has(placement.major)) major.set(placement.major, major.size);
    if (minor.has(placement.minor)) continue;
    const next = perMajor.get(placement.major) ?? 0;
    minor.set(placement.minor, next);
    perMajor.set(placement.major, next + 1);
  }
  return { major, minor };
}

/**
 * Does this cell carry a status the user actually recorded? The wall's name for the shared
 * predicate — the rule itself lives once in `presence-quick-entry.ts`, with the three looks.
 * Kept as its own export because the wall reads it on a possibly-absent row and because that
 * is the vocabulary its callers use (`hatch`, `statusCounts`, `isSettledAt`).
 */
export function isRecordedCell(row: StudentRow | undefined): boolean {
  return isRecorded(row);
}

/** Status of a cell for painting, or undefined when nothing is recorded there. */
export function recordedStatus(row: StudentRow | undefined): PresenceStatus | undefined {
  return isRecordedCell(row) ? row!.status : undefined;
}

/**
 * The day strip of one student: one mark per period, telling recorded from not. It is what
 * lets the wall show a single period without losing sight of the day — the matrix of every
 * period against every student is precisely what does not fit at a hundred students.
 */
export function dayStrip(
  frequentationId: string,
  periodIds: readonly string[],
  getRow: (frequentationId: string, periodId: string) => StudentRow | undefined,
): { periodId: string; recorded: boolean; present: boolean }[] {
  return periodIds.map(periodId => {
    const row = getRow(frequentationId, periodId);
    return { periodId, recorded: isRecordedCell(row), present: !!row };
  });
}

/** « 3/4 » — periods recorded today out of those the student has. */
export function dayStripLabel(strip: readonly { recorded: boolean; present: boolean }[]): string {
  const owned = strip.filter(s => s.present);
  if (!owned.length) return '';
  return `${owned.filter(s => s.recorded).length}/${owned.length}`;
}

/** What a row contributes to the tiling, on whichever axis is chosen. */
export interface WallPlacement {
  major: string;
  minor: string;
  majorLabel: string;
  minorLabel: string;
}

/** What the axes need from the outside — neither lives on the row itself. */
export interface WallPlacementContext {
  /** The competence numbers the student works today. */
  modulesOf: (row: StudentRow) => readonly string[];
  /** The recorded status for the shown period, or null when nothing is recorded. */
  statusOf: (row: StudentRow) => string | null;
  /** French label of a status key. */
  statusLabel: (status: string) => string;
}

const NO_COMPETENCY = 'Sans compétence';
const UNRECORDED = 'À saisir';

/**
 * Where a row falls on the chosen axis. One place, so the packing, the labels and the hues
 * can never disagree about what a territory is.
 *
 * `none` puts everyone in one territory: the wall then draws no outline at all, which is what
 * « aucun regroupement » means — a plain wall of cells in the requested order.
 */
export function wallPlacement(
  row: StudentRow,
  grouping: WallGrouping,
  context: WallPlacementContext,
): WallPlacement {
  switch (grouping) {
    case 'program':
      return {
        major: programKey(row),
        minor: groupKey(row),
        majorLabel: programLabel(row),
        minorLabel: groupLabel(row),
      };
    case 'competency': {
      // The first competence of the day, which is the one the cell shows first too. A student
      // working none still needs a territory, or the minor level disappears for them alone.
      const first = [...context.modulesOf(row)].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))[0];
      return {
        major: programKey(row),
        minor: first ? `c:${programKey(row)}|${first}` : `c:${programKey(row)}|-`,
        majorLabel: programLabel(row),
        minorLabel: first ?? NO_COMPETENCY,
      };
    }
    case 'status': {
      const status = context.statusOf(row);
      return {
        major: status ?? 'unconfirmed',
        minor: `${status ?? 'unconfirmed'}|${programKey(row)}`,
        majorLabel: status ? context.statusLabel(status) : UNRECORDED,
        minorLabel: programLabel(row),
      };
    }
    case 'none':
      // One territory for everyone, but the programme still has to be readable: with no
      // outline to name, the cell itself carries it — on EVERY cell, since there is no
      // territory whose first cell could hold it once.
      return { major: 'all', minor: 'all', majorLabel: programLabel(row), minorLabel: '' };
  }
}

/** One entry of the competence filter: the number is the value, the label carries the title. */
export interface CompetencyOption {
  value: string;
  title: string;
}

/**
 * The competence filter's entries, for the competences actually ON SCREEN — filtering by one
 * nobody is working today would only ever empty the wall.
 *
 * The title comes from the program's module list; a number with no match keeps the number
 * alone rather than disappearing, because the cell shows it either way. Sorted by number,
 * which is how the numbers read on the cells.
 */
export function competencyOptions(
  numbersOnScreen: Iterable<string>,
  modules: readonly { number: string; title: string }[],
): CompetencyOption[] {
  const titleByNumber = new Map<string, string>();
  for (const m of modules) {
    if (!titleByNumber.has(m.number)) titleByNumber.set(m.number, m.title);
  }
  return [...new Set(numbersOnScreen)]
    .sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
    .map(value => {
      const title = titleByNumber.get(value);
      return { value, title: title ? `${value} — ${title}` : value };
    });
}

/**
 * Does a student pass the competence filter? An empty selection keeps everyone; otherwise the
 * student must work at least ONE of the chosen competences — a union, not an intersection: a
 * teacher picking two competences wants the students of both, not the rare few doing both.
 */
export function matchesCompetencies(selected: readonly string[], own: readonly string[]): boolean {
  if (!selected.length) return true;
  return own.some(number => selected.includes(number));
}

/**
 * Which cells carry the territory labels. A label says WHERE one is, so it appears ONCE per
 * territory — on the first cell of the path that enters it — rather than on all forty-four.
 * Returns the index sets, keyed by the position of the cell in path order.
 */
export function territoryLabelPositions(
  cells: readonly { major: string; minor: string }[],
): { major: Set<number>; minor: Set<number> } {
  const major = new Set<number>();
  const minor = new Set<number>();
  const seenMajor = new Set<string>();
  const seenMinor = new Set<string>();
  cells.forEach((cell, i) => {
    if (!seenMajor.has(cell.major)) {
      seenMajor.add(cell.major);
      major.add(i);
    }
    if (!seenMinor.has(cell.minor)) {
      seenMinor.add(cell.minor);
      minor.add(i);
    }
  });
  return { major, minor };
}
